"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseNode = void 0;
const jsonpath_plus_1 = require("jsonpath-plus");
const RecursiveSetObjectValue_1 = require("../utils/RecursiveSetObjectValue");
const types_1 = require("./types");
const lodash_1 = __importDefault(require("lodash"));
/**
 * The BaseNode class defines both the structure of the transform node used for configuration,
 * as well as the implementation of a transform node
 */
class BaseNode {
    constructor(
    // required options for running the transform node
    runtimeOptions, 
    // options only needed for design time
    designOptions) {
        this.setConfigs = (newConfig) => {
            this.config = Object.assign({}, newConfig);
        };
        this.setConfigValue = (key, value) => {
            // we have to use casts here in order to avoid the union of classes negating all possible config values
            this.config[key] = value;
        };
        this.getConfigValue = (key) => {
            return this.config[key];
        };
        /**
         * This sets a given input key to the value provided. The key is expected to be a JSONPath style variable
         *
         * @param key The top level key to subsitute
         * @param value The (potentially nested) value to set it to
         */
        this.setInputValue = (key, value) => {
            // we have to cast here, because of the recursiveness of the function
            this.inputReferences = (0, RecursiveSetObjectValue_1.recursiveSetObjectValue)(this.inputReferences, key, value);
        };
        this.setInputReference = (inputRef) => {
            this.inputReferences = inputRef;
        };
        this.getInputReference = () => {
            return this.inputReferences;
        };
        this.deleteInput = (parentKey) => {
            this.inputReferences[parentKey].pop();
        };
        //TODO: Move this into a utility function
        this.getInputValue = (key) => {
            return this.inputReferences && lodash_1.default.get(this.inputReferences, key);
        };
        this.getConfiguration = (x, y) => {
            var _a;
            return {
                //use the static method for this
                type: this.constructor.nodeDefinition.NODE_TYPE,
                config: this.config,
                inputs: this.inputReferences,
                name: (_a = this.config) === null || _a === void 0 ? void 0 : _a.name,
                position: (Number.isFinite(x) && Number.isFinite(y)) ? {
                    x,
                    y
                } : undefined
            };
        };
        // ************************   Run time methods
        this.synchronousGetInputValue = (nodeMap, sourceDef) => {
            //The sourceDef is a potentially recursive data structure. Each node of the structure can be an object with keys that are other
            // sourceDefs, a supported atomic data type, or a collection of the supported atmoic types
            if (sourceDef !== undefined) {
                if ((0, types_1.isNodeDataReference)(sourceDef)) {
                    // if this is a reference, determine wht the reference points to and return that value
                    const foundSourceObject = nodeMap[sourceDef.sourceObject];
                    if (foundSourceObject) {
                        const source = foundSourceObject(nodeMap, sourceDef.sourceObject);
                        if (source) {
                            // make sure we aren't a promise
                            // Skip this for code coverage. Should only occur in development at the moment
                            // istanbul ignore next
                            if ('then' in source && typeof source.then === 'function') {
                                throw new Error('Promises not allowed in synchronous/idempotent data maps');
                            }
                            //use jsonpath to get value from object
                            const result = (0, jsonpath_plus_1.JSONPath)({ path: sourceDef.sourcePath, json: source });
                            return result && result.length > 0 ? result[0] : undefined;
                        }
                    }
                    return undefined;
                }
                else if (typeof sourceDef === 'object' &&
                    !(sourceDef instanceof Date) &&
                    !Array.isArray(sourceDef) &&
                    sourceDef !== null) {
                    // if we are a plain object, return the value for each key found within that object recursively
                    const ob = {};
                    Object.keys(sourceDef).forEach(key => {
                        ob[key] = this.synchronousGetInputValue(nodeMap, sourceDef[key]);
                    }, {});
                    return ob;
                }
                else if (sourceDef === null) {
                    return null;
                }
                else if (Array.isArray(sourceDef)) {
                    // Arrays should either be a single node reference to an array, or an array of values which are either fixed strings, or data references
                    // This path is when it's not a single node ref, but an array of references or values
                    const newValue = [];
                    sourceDef.forEach(singleVal => {
                        newValue.push(this.synchronousGetInputValue(nodeMap, singleVal));
                    });
                    return newValue;
                }
                else if (typeof sourceDef !== 'object' || sourceDef instanceof Date) {
                    // if we aren't an object and are just one of the other supportedTypes, then just return ourself
                    return sourceDef;
                }
            }
            return undefined;
        };
        this.asynchronousGetInputValue = async (nodeMap, sourceDef) => {
            if (sourceDef !== undefined) {
                if ((0, types_1.isNodeDataReference)(sourceDef)) {
                    const foundSourceObject = nodeMap[sourceDef.sourceObject];
                    if (foundSourceObject) {
                        const source = await foundSourceObject(nodeMap, sourceDef.sourceObject);
                        if (source) {
                            //use jsonpath to get value from object
                            const result = (0, jsonpath_plus_1.JSONPath)({ path: sourceDef.sourcePath, json: source });
                            return Promise.resolve(result && result.length > 0 ? result[0] : undefined);
                        }
                    }
                    return undefined;
                }
                else if (typeof sourceDef === 'object' &&
                    !(sourceDef instanceof Date) &&
                    !Array.isArray(sourceDef) &&
                    sourceDef !== null) {
                    const resultObject = Promise.all(Object.keys(sourceDef).map(async (key) => {
                        return { [key]: await this.asynchronousGetInputValue(nodeMap, sourceDef[key]) };
                    })).then(results => {
                        const finalResult = {};
                        results.forEach(item => {
                            Object.assign(finalResult, item);
                        });
                        return finalResult;
                    });
                    // This cast should technically be correct, because we are collapsing the node sourced results into static results
                    return resultObject;
                }
                else if (sourceDef === null) {
                    return Promise.resolve(null);
                }
                else if (Array.isArray(sourceDef)) {
                    // Arrays should either be a single node reference to an array, or an array of values which are either fixed strings, or data references
                    // This path is when it's not a single node ref, but an array of references or values
                    return Promise.all(sourceDef.map(curVal => {
                        return this.asynchronousGetInputValue(nodeMap, curVal);
                    })).then(results => {
                        return Promise.resolve(results);
                    });
                }
                else if (typeof sourceDef !== 'object' || sourceDef instanceof Date) {
                    // if we aren't an object and are just one of the other supportedTypes, then just return ourself
                    return Promise.resolve(sourceDef);
                }
            }
            return undefined;
        };
        this.synchronousResolveInputs = (nodeMap) => {
            //  Having to do this and cast, since TS can't guarantee the types will match
            const inputValues = {};
            if (this.inputReferences) {
                // cycle through all of the input params
                Object.keys(this.inputReferences).forEach(key => {
                    const valueReference = this.inputReferences[key];
                    if (Array.isArray(valueReference)) {
                        // Arrays should either be a single node reference to an array, or an array of values which are either fixed strings, or data references
                        // This path is when it's not a single node ref, but an array of references or values
                        const newValue = [];
                        valueReference.forEach(singleVal => {
                            newValue.push(this.synchronousGetInputValue(nodeMap, singleVal));
                        });
                        inputValues[key] = newValue;
                    }
                    else {
                        inputValues[key] = this.synchronousGetInputValue(nodeMap, valueReference);
                    }
                });
            }
            return inputValues;
        };
        this.synchronousResolver = (nodeMap, configFunctions, seedData) => {
            try {
                const inputValues = this.synchronousResolveInputs(nodeMap);
                return this._resolve(inputValues, configFunctions, seedData);
            }
            catch (e) {
                console.error('Sync resolver error: ', e, '\r\nInput refs:', this.inputReferences);
                throw e;
            }
        };
        this.asynchronousResolveInputs = async (nodeMap) => {
            if (this.inputReferences && Object.keys(this.inputReferences).length > 0) {
                // cycle through all of the input params
                const inputValuePromises = Object.keys(this.inputReferences).map(key => {
                    const value = this.inputReferences[key];
                    if (Array.isArray(value)) {
                        // Arrays should either be a single node reference to an array, or an array of values which are either fixed strings, or data references
                        // This path is when it's not a single node ref, but an array of references or values
                        //return {};
                        return Promise.all(value.map(curVal => {
                            return this.asynchronousGetInputValue(nodeMap, curVal);
                        })).then(results => {
                            return { [key]: results };
                        });
                    }
                    else {
                        return this.asynchronousGetInputValue(nodeMap, value).then(value => {
                            return { [key]: value };
                        });
                    }
                });
                const results = await Promise.all(inputValuePromises);
                const output = {};
                results.forEach(val => {
                    Object.assign(output, val);
                });
                return output;
            }
            return Promise.resolve({});
        };
        //TODO: build a synchronous and asynchronous getAllInputs
        this.asynchronousResolver = async (nodeMap, configFunctions, seedData) => {
            const inputs = await this.asynchronousResolveInputs(nodeMap);
            try {
                return this._resolve(inputs, configFunctions, seedData);
            }
            catch (e) {
                console.error('Async resolver error: ', e, '\r\nInput refs:', this.inputReferences);
                return null;
            }
        };
        this._resolve = () => {
            // ignore this pathway for code coverage, since it should only occur with misconfigured nodes
            // istanbul ignore next
            throw new Error(`If a node doesn't override the synchronous and asynchronousResolver, it must provide its own _resolve implementation.`);
        };
        // cast here, so we can use a generic record on the constructor which makes instantiating more straight forward for consuming services
        this.config = ((runtimeOptions === null || runtimeOptions === void 0 ? void 0 : runtimeOptions.config) || {});
        this.inputReferences = runtimeOptions === null || runtimeOptions === void 0 ? void 0 : runtimeOptions.inputs;
        this.accessorMap = runtimeOptions === null || runtimeOptions === void 0 ? void 0 : runtimeOptions.accessorMap;
        this.mapScenarios = runtimeOptions === null || runtimeOptions === void 0 ? void 0 : runtimeOptions.mapScenarios;
        this.schemaLookup = designOptions === null || designOptions === void 0 ? void 0 : designOptions.schemaLookup;
    }
    static get nodeDefinition() {
        // ignore this pathway for code coverage, since it should only occur with misconfigured nodes
        // istanbul ignore if
        if (!this._nodeDefinition) {
            throw new Error('All nodes must define a static node definition');
        }
        return this._nodeDefinition;
    }
    get interfaceDefinition() {
        // ignore this pathway for code coverage, since it should only occur with misconfigured nodes
        // istanbul ignore if
        if (!this._interfaceDefinition) {
            throw new Error('All transform nodes must define an interface definition');
        }
        return this._interfaceDefinition.definitions;
    }
}
exports.BaseNode = BaseNode;
BaseNode.getProp = (type, key) => {
    switch (type) {
        case 'String Array':
            return { type: 'array', items: { type: 'string', description: key } };
        case 'Boolean Array':
            return { type: 'array', items: { type: 'boolean', description: key } };
        case 'Number Array':
            return { type: 'array', items: { type: 'number', description: key } };
        case 'GeoJSON Array':
            return { type: 'array', items: { description: key, $ref: '/Geography/GeoJSON', type: 'object' } };
        case 'Photo Array':
            return { type: 'array', items: { type: 'string', format: 'photo', description: key } };
        case 'Date Array':
            return { type: 'array', items: { type: 'string', format: 'date-time', description: key } };
        case 'Object Array':
            return { type: 'array', items: { type: 'object', properties: {}, description: key } };
        case 'String':
            return { type: 'string', description: key };
        case 'Boolean':
            return { type: 'boolean', description: key };
        case 'Number':
            return { type: 'number', description: key };
        case 'GeoJSON':
            return {
                description: key,
                $ref: '/Geography/GeoJSON',
                type: 'object',
            };
        case 'Photo':
            return { type: 'string', format: 'photo', description: key };
        case 'Date':
            return { type: 'string', format: 'date-time', description: key };
        default:
            return { type: 'object', properties: {}, description: key };
    }
};
BaseNode.schemaMatchesType = (type, schema, numberOfProperties, invalidNames) => {
    let typeName = '';
    let format = '';
    let ref = '';
    let isArray = false;
    switch (type) {
        case 'String Array':
            isArray = true;
            typeName = 'string';
            break;
        case 'Boolean Array':
            isArray = true;
            typeName = 'boolean';
            break;
        case 'Number Array':
            isArray = true;
            typeName = 'number';
            break;
        case 'GeoJSON Array':
            isArray = true;
            typeName = 'object';
            ref = '/Geography/GeoJSON';
            break;
        case 'Photo Array':
            isArray = true;
            typeName = 'string';
            format = 'photo';
            break;
        case 'Date Array':
            isArray = true;
            typeName = 'string';
            format = 'date-time';
            break;
        case 'Object Array':
            isArray = true;
            typeName = 'object';
            break;
        case 'String':
            typeName = 'string';
            break;
        case 'Boolean':
            typeName = 'boolean';
            break;
        case 'Number':
            typeName = 'number';
            break;
        case 'GeoJSON':
            typeName = 'object';
            ref = '/Geography/GeoJSON';
            break;
        case 'Photo':
            typeName = 'string';
            format = 'photo';
            break;
        case 'Date':
            typeName = 'string';
            format = 'date-time';
            break;
        default:
            typeName = 'object';
            break;
    }
    if (Object.keys(schema.properties).length === numberOfProperties) {
        return Object.keys(schema.properties).find(k => {
            if (invalidNames && invalidNames.includes(k)) {
                return;
            }
            let s = schema.properties[k];
            if (isArray) {
                if (s.type == 'array') {
                    s = s.items;
                }
                else {
                    return;
                }
            }
            return (type === 'Any' ||
                (s.type === typeName &&
                    ((format && format === s.format) || (!format && !s.format)) &&
                    ((ref && ref === s.$ref) || (!ref && !s.$ref))));
        });
    }
    return undefined;
};
BaseNode.getTypeOptions = (includeArray) => {
    if (includeArray) {
        return {
            description: 'Type',
            type: 'string',
            enum: [
                'Boolean',
                'Boolean Array',
                'Date',
                'Date Array',
                'GeoJSON',
                'GeoJSON Array',
                'Number',
                'Number Array',
                'Object',
                'Object Array',
                'Photo',
                'Photo Array',
                'String',
                'String Array',
            ],
            default: 'Object',
        };
    }
    else {
        return {
            description: 'Type',
            type: 'string',
            enum: ['Boolean', 'Date', 'GeoJSON', 'Number', 'Object', 'Photo', 'String'],
            default: 'Object',
        };
    }
};
//# sourceMappingURL=BaseNode.js.map